import { formatDistanceToNow } from 'date-fns';
import type { TaskCard } from '../../services/api/board';
import { getTierColor, chipTextColor } from '../../utils/format';

const TIER_LABEL: Record<number, string> = {
  1: 'URGENT',
  2: 'HIGH',
  3: 'NORMAL',
  4: 'LOW',
};

export interface StatusPillConfig {
  dot: string;
  bg: string;
  text: string;
  label: string;
}

export interface LastComment {
  authorExternalUserId: number | null;
}

export type ExtendedTaskCard = TaskCard & {
  commentCount?: number;
  lastComment?: LastComment;
};

interface RequestRowProps {
  task: ExtendedTaskCard;
  selected: boolean;
  onSelect: (id: number) => void;
  /** Resolved pill config for the status this task is in */
  pillConfig?: StatusPillConfig;
}

export function RequestRow({ task, selected, onSelect, pillConfig }: RequestRowProps) {
  const tierBg = task.tier ? getTierColor(task.tier.orderIndex) : null;
  const tierLabel = task.tier ? (TIER_LABEL[task.tier.orderIndex] ?? null) : null;
  const tierTextColor = tierBg ? chipTextColor(tierBg) : '#ffffff';

  const isWaitingOnYou =
    task.lastComment !== undefined && task.lastComment.authorExternalUserId === null;

  const relativeTime = task.createdAt
    ? formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(task.id)}
      className={[
        'w-full text-left px-4 py-3 border-b border-ms-border transition-colors',
        selected ? 'bg-ms-surface-2' : 'hover:bg-ms-surface-2',
      ].join(' ')}
      style={
        selected && tierBg
          ? { borderLeft: `4px solid ${tierBg}` }
          : selected
            ? { borderLeft: '4px solid #6b7280' }
            : undefined
      }
    >
      {/* Top row: tier badge + code + timestamp */}
      <div className="flex items-center gap-2 mb-1">
        {tierBg && tierLabel ? (
          <span
            className="text-[10px] font-semibold font-mono uppercase tracking-wide px-1.5 py-0.5 rounded"
            style={{ backgroundColor: tierBg, color: tierTextColor }}
          >
            {tierLabel}
          </span>
        ) : null}
        <span className="text-xs text-ms-text-secondary font-mono">{task.code ?? ''}</span>
        <span className="ml-auto text-xs text-ms-text-secondary">{relativeTime}</span>
      </div>

      {/* Title */}
      <p className="text-sm font-medium text-ms-text-strong truncate mb-1">{task.name}</p>

      {/* Bottom row: status pill + waiting-on-you + comment count */}
      <div className="flex items-center gap-2">
        {pillConfig ? (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${pillConfig.bg} ${pillConfig.text}`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: pillConfig.dot }}
            />
            {pillConfig.label}
          </span>
        ) : null}

        {isWaitingOnYou ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-purple-500" />
            Waiting on you
          </span>
        ) : null}

        {typeof task.commentCount === 'number' ? (
          <span className="ml-auto text-xs text-ms-text-secondary">{task.commentCount}</span>
        ) : null}
      </div>
    </button>
  );
}
